// The supplier portal frame (Lane 1331; new look Lane 1394): top bar, the
// person's name, Sign out.
//
// The signed-in shell loads /me once and shares it with the page through a
// small context of its own. It never reads the buyer AuthContext.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { portalApi, getPortalToken, clearPortalToken, PORTAL_HOME, PORTAL_LOGIN } from './portalApi';
import WingmanLogo from '../../components/WingmanLogo';
import '../ListLinear.css';
import './SourcingPortal.css';

const PortalSession = createContext(null);

export function usePortalSession() {
  return useContext(PortalSession);
}

function TopBar({ user, onSignOut, signingOut }) {
  return (
    <div className="srcp-bar">
      <Link to={user ? PORTAL_HOME : PORTAL_LOGIN} className="srcp-brand">
        <WingmanLogo size={30} style={{ height: 'auto', flex: 'none' }} />
        <span>Wingman <span className="srcp-brand-sub">Supplier portal</span></span>
      </Link>
      {user && (
        <span className="srcp-bar-right">
          <span className="srcp-bar-name">{user.full_name || user.email}</span>
          <button type="button" className="srcp-btn srcp-btn--sm" disabled={signingOut} onClick={onSignOut}>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </span>
      )}
    </div>
  );
}

// A buyer's mark (Lane 1442): the first letters of the buyer's name in a dark square,
// so every buyer company has one with no logo file to keep (any Wingman customer).
export function BuyerMark({ name, size = 32 }) {
  const first = String(name || '?').trim().split(/\s+/)[0] || '?';
  const text = first.replace(/[^A-Za-z0-9&]/g, '').slice(0, 4).toUpperCase() || '?';
  return (
    <span className="srcp-buyermark" aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size / (text.length > 3 ? 3.4 : 2.8))) }}>
      {text}
    </span>
  );
}

// The sign-in, join and reset pages (Lane 1442, Mobbin: Stripe, Snowflake, Quo): one
// centered card with a mark on top, a title and a short line, then the form.
// `mark`: the Wingman logo unless given (the join page shows the buyer's mark).
export function AuthCard({ mark = null, title, subtitle = null, onSubmit = null, children }) {
  const Box = onSubmit ? 'form' : 'div';
  return (
    <Box className="srcp-auth" onSubmit={onSubmit || undefined} noValidate={onSubmit ? true : undefined}>
      <div className="srcp-auth-mark">{mark || <WingmanLogo size={46} style={{ height: 'auto' }} />}</div>
      <h1 className="srcp-auth-t">{title}</h1>
      {subtitle && <div className="srcp-auth-sub">{subtitle}</div>}
      <div className="srcp-auth-b">{children}</div>
    </Box>
  );
}

// For the join and sign-in pages: the frame without a session.
export function PublicShell({ children }) {
  return (
    <div className="ll srcp-app">
      <TopBar user={null} />
      <main className="srcp-wrap srcp-wrap--narrow">{children}</main>
    </div>
  );
}

export default function PortalShell({ children }) {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [error, setError] = useState('');
  const [signingOut, setSigningOut] = useState(false);
  const hasToken = Boolean(getPortalToken());

  const reload = useCallback(() => portalApi.me()
    .then((r) => { setMe(r); setError(''); return r; })
    .catch((e) => { if (e.status !== 401) setError(e.message); return null; }), []);

  useEffect(() => {
    if (!hasToken) { navigate(PORTAL_LOGIN, { replace: true }); return; }
    reload();
  }, [hasToken, navigate, reload]);

  const signOut = async () => {
    setSigningOut(true);
    try { await portalApi.logout(); } catch (e) { /* the token is cleared below either way */ }
    clearPortalToken();
    navigate(PORTAL_LOGIN, { replace: true });
  };

  if (!hasToken) return null;
  return (
    <div className="ll srcp-app">
      <TopBar user={me && me.user} onSignOut={signOut} signingOut={signingOut} />
      <main className="srcp-wrap">
        {error && <div className="srcp-error" role="alert">{error}</div>}
        {!me && !error && <div className="srcp-loading">Loading…</div>}
        {me && <PortalSession.Provider value={{ me, reload }}>{children}</PortalSession.Provider>}
      </main>
    </div>
  );
}
